import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { GalleryDto, GalleryPublicAccessLevel, TenantDto } from '@nusign/picflow-backend-types';
import { from, map, of, switchMap } from 'rxjs';
import { GalleryAccessLevelService } from '../../../services/gallery-access-level.service';
import { GalleryService } from '../../../services/gallery.service';
import { StateService } from '../../../services/state.service';
import { GalleryShareContextComponent } from '../../../shared-extra/components/gallery-share-context/gallery-share-context.component';
import { AccessItemCtaComponent } from '../access-item-cta/access-item-cta.component';
import { AccessItemHeaderComponent } from '../access-item-header/access-item-header.component';
import { AccessItemComponent } from '../access-item/access-item.component';

@Component({
  selector: 'app-gallery-public-edit-access-item',
  imports: [AccessItemComponent, AccessItemHeaderComponent, AccessItemCtaComponent, GalleryShareContextComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gallery-public-edit-access-item.component.html',
  styleUrl: './gallery-public-edit-access-item.component.scss',
})
export class GalleryPublicEditAccessItemComponent {
  private galleryService = inject(GalleryService);
  private accessLevelService = inject(GalleryAccessLevelService);
  private state = inject(StateService);
  gallery = input.required<GalleryDto>();
  galleryPermissions = input.required<GalleryDto['permissions']>();
  editLink = output<void>();
  publicAccessLevels: GalleryPublicAccessLevel[] = ['review', 'view_only', 'no_access'];
  publicAccessLevel = toSignal(
    toObservable(this.gallery).pipe(
      switchMap(gallery => {
        if (gallery.public_access) return of(gallery.public_access);
        if (gallery.public_access_effective) return of(gallery.public_access_effective);
        return from(this.accessLevelService.getAccessLevels(gallery)).pipe(map(levels => levels[1]));
      })
    )
  );

  isTeamMember = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    return this.state.tenants?.some(t => t.id === tenantId);
  });

  iconColor = computed(() => {
    const publicAccessLevel = this.publicAccessLevel();
    if (!publicAccessLevel) return;
    if (publicAccessLevel === 'no_access') return 'red';
    return 'green';
  });

  changePublicAccessLevel(accessLevel: GalleryPublicAccessLevel) {
    this.galleryService.updateGallery(this.gallery().id, {
      public_access: accessLevel,
    });
  }
}
